import * as React from "react";
import { useTranslation } from "react-i18next";

import { BorderColor, Label } from "@mui/icons-material";
import {
  Button,
  ButtonGroup,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Tooltip,
} from "@mui/material";
import { DataGrid, GridCellParams, GridColDef } from "@mui/x-data-grid";
import { DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import axios from "axios";
import dayjs from "dayjs";

import useSnackbarAlert from "../../../utils/snackbarHandlers";
import CommonButton from "../../common/button/Button";
import CommonInput from "../../common/input/InputBox";

const SalesInventory: React.FC<{}> = () => {
  //@ts-ignore
  const config = window.config;
  const apiBaseURL = config?.server || "http://18.217.95.31:8080";
  let localData = localStorage.getItem("UserData");
  const shopownerData = localData ? JSON.parse(localData || "") : null;
  const currencySymbol = config.currencySymbol;

  const { t } = useTranslation("translation");
  const [loading, setLoading] = React.useState<boolean>(false);
  const [date, setDate] = React.useState<any>(dayjs(new Date()));
  const { showErrorMessage, showSuccessMessage } = useSnackbarAlert();
  const [ticketSalesRecords, setTicketSalesRecords] = React.useState<Array<any>>([]);
  const [openTicketChangePopup, setOpenTicketChangePopup] = React.useState<boolean>(false);
  const [popupTicketDetails, setPopupTicketDetails] = React.useState<any>({});
  const [endSalesNumber, setEndSalesNumber] = React.useState<any>("");
  const [errors, setErrors] = React.useState("");
  const [updateTicketLoading, setUpdateTicketLoading] = React.useState<boolean>(false);
  const [totalSalesTicketCount, setTotalSalesTicketCount] = React.useState(0);
  const [totalSalesPrice, setTotalSalesPrice] = React.useState(0);
  const [endDayPopupOpen, setEndDayPopupOpen] = React.useState<boolean>(false);

  const fetchTicketSalesRecords = () => {
    setLoading(true);
    let formattedDate = date.toISOString();
    formattedDate = formattedDate?.split("T")[0];

    const payload = {
      date: formattedDate,
      shopId: shopownerData?.uId,
    };
    axios
      .post(`${apiBaseURL}/api/ticketSales/fetchAllTempSalesRecord`, payload)
      .then((response: any) => {
        if (response?.data?.sales !== "") {
          let ticketSalesSum = 0;
          let ticketSalesPrice = 0;
          setTicketSalesRecords(response?.data?.sales);
          response?.data?.sales?.map((item: any) => {
            ticketSalesSum = ticketSalesSum + parseInt(item.tempsalesticketSold);
            ticketSalesPrice = ticketSalesPrice + parseInt(item.tempsalesindividualTotalAmount);
          });

          setTotalSalesTicketCount(ticketSalesSum);
          setTotalSalesPrice(ticketSalesPrice);
        }
      })
      .catch((error) => {
        showErrorMessage(error);
      })
      .finally(() => {
        setLoading(false);
        setOpenTicketChangePopup(false);
      });
  };

  React.useEffect(() => {
    fetchTicketSalesRecords();
  }, []);

  const openTicketUpdateMechanism = (event: any, params: any) => {
    setOpenTicketChangePopup(true);
    setPopupTicketDetails(params);
    setEndSalesNumber(params.tempsalesendSalesNumber);
  };

  const updateTicketDetailsMechanism = () => {
    setUpdateTicketLoading(true);
    const ticketDiff =
      parseInt(endSalesNumber) - parseInt(popupTicketDetails?.tempsalesstartSalesNumber);

    const payload = {
      endSalesNumber: endSalesNumber,
      isSoldOut: popupTicketDetails.tempsalesisSoldOut,
      ticketSold: ticketDiff,
      ticketValue: popupTicketDetails.ticketPrice,
      individualTotalAmount: parseInt(popupTicketDetails.ticketPrice) * ticketDiff,
    };
    console.log("Popup ticket details - ", popupTicketDetails);
    console.log("Payload - ", payload);

    axios
      .put(
        `${apiBaseURL}/api/ticketSales/updateTempSalesRecord?id=${popupTicketDetails.tempsalesid}`,
        payload,
      )
      .then((response) => {
        console.log("Response - ", response);
        if (response?.data?.salesRecord?.length > 0) {
          // const record = response?.data?.salesRecord?.[0];
          // const currentRecords = [...ticketSalesRecords];
          // const index = currentRecords.findIndex((item) => item.id === record.id);
          // if(index !== -1) {
          //     currentRecords[index] = record;
          // }
          // setTicketSalesRecords([...currentRecords]);
          showSuccessMessage(t("salesRecordUpdateMessage"));
          fetchTicketSalesRecords();
          // setOpenTicketChangePopup(false);
        }
      })
      .catch((error) => {
        console.log("Error - ", error);
        showErrorMessage(error);
      })
      .finally(() => setUpdateTicketLoading(false));
  };

  const handleChange = (e: any) => {
    if (e.target.value < popupTicketDetails.tempsalesstartSalesNumber) {
      setErrors("End sales number should not be less then start sales number");
    } else {
      setErrors("");
      setEndSalesNumber(e.target.value);
    }
  };

  const endDayConfirmation = () => {
    setEndDayPopupOpen(true);
  }

  const endDayMainMechanism = () => {
    let formattedDate = date.toISOString();
    formattedDate = formattedDate?.split("T")[0];

    const payload = {
      date: formattedDate,
      shopId: shopownerData?.uId,
    };

    axios.post(`${apiBaseURL}/api/ticketSales/saveRecordsOnDayEnd`, payload)
    .then((response) => {
        showSuccessMessage("Day ended successfully");
        setTicketSalesRecords([]);
    })
    .catch((error) => {
        showErrorMessage(error);
    })
    .finally(() => {
        setEndDayPopupOpen(false);
    });
  }

  const columns: GridColDef[] = [
    {
      field: "tempsalesgameNumber",
      headerName: "Game Number",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "ticketImage",
      headerName: "Image",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <img width={100} height={100} src={params?.row?.ticketImage}></img>;
      },
    },
    {
      field: "packNumber",
      headerName: "Pack Number",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "numberOfPack",
      headerName: "Pack quantity",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "tempsalesstartSalesNumber",
      headerName: "Start",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "tempsalesendSalesNumber",
      headerName: "End",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "tempsalesisSoldOut",
      headerName: "Is Sold Out",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "tempsalesticketSold",
      headerName: "Tickets sold",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "tempsalesticketValue",
      headerName: "Ticket value",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "tempsalesindividualTotalAmount",
      headerName: "Total amount",
      width: 200,
      align: "center",
      headerAlign: "center",
      flex: 1,
    },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      width: 200,
      renderCell: (params: GridCellParams) => {
        console.log("Date - ", date.toISOString().split("T")[0], typeof date.toISOString().split("T")[0]);
        console.log("Generated date - ", new Date().toISOString().split("T")[0], typeof new Date().toISOString().split("T")[0]);
        console.log("Compare - ", date.toISOString().split("T")[0] === new Date().toISOString().split("T")[0]);
        return (
          <ButtonGroup size='small' color='inherit' aria-label='group action buttons'>
            <Tooltip title='Edit'>
              <Button onClick={(e) => openTicketUpdateMechanism(e, params.row)} disabled={date.toISOString().split("T")[0] !== new Date().toISOString().split("T")[0]}>
                <BorderColor fontSize='small' />
              </Button>
            </Tooltip>
          </ButtonGroup>
        );
      },
    },
  ];

  return (
    <>
      <Grid display={"flex"} justifyContent={"center"} className='mt-2'>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DemoContainer components={["DatePicker"]}>
            <DatePicker
              className='date-field'
              name='date'
              label={t("dateLabel")}
              onChange={(newValue) => {
                setDate(dayjs(newValue));
              }}
              value={date}
              sx={{ width: "50%" }}
            />
          </DemoContainer>
        </LocalizationProvider>

        <Grid className='w-[10%] h-[10%] mt-4'>
          <CommonButton
            id='submit-button'
            className='login-button'
            variant='contained'
            size='small'
            disabled={loading}
            onClick={() => {
              fetchTicketSalesRecords();
            }}
          >
            {t("searchButton")}
            {loading && (
              <CircularProgress
                color='inherit'
                size={14}
                thickness={6}
                style={{ marginLeft: "8px" }}
              />
            )}
          </CommonButton>
        </Grid>

        <Tooltip title={t("endDayTooltipText")}>
        <Grid className='w-[10%] h-[10%] mt-4 ml-8'>
          <CommonButton
            id='submit-button'
            className='login-button'
            variant='contained'
            size='small'
            disabled={date.toISOString().split("T")[0] !== new Date().toISOString().split("T")[0]}
            onClick={() => endDayConfirmation()}
          >
            {t("endDayButtonText")}
          </CommonButton>
        </Grid>
        </Tooltip>
        {/* <Grid className="self-center h-[10%]"><span>Note - Please press this button at end of the day</span></Grid> */}
      </Grid>

      <Grid
        className='w-full'
        style={{
          marginTop: "1%",
          height: "630px",
        }}
        display={"flex"}
        flexDirection={"column"}
      >
        <DataGrid
          rows={ticketSalesRecords}
          loading={loading}
          columns={columns}
          rowCount={ticketSalesRecords.length}
          pageSizeOptions={[10, 20, 50, 100, 500]}
          // pagination
          // paginationMode="server"
          // paginationModel={paginationModel}
          // onPaginationModelChange={setPaginationModel}
          getRowId={(row) => row.tempsalesid}
          disableColumnSelector
          disableRowSelectionOnClick
          sx={{
            "& .MuiDataGrid-main": {
              borderColor: "#DAA520",
            },
            "& .MuiDataGrid-columnHeader": {
              backgroundColor: "#F5F5E6",
            },
            "& .MuiDataGrid-columnHeaderTitle": {
              fontWeight: 600,
            },
            "& .MuiDataGrid-row.Mui-odd": {
              backgroundColor: "#F5F5E6",
            },
            // maxWidth: "fit-content",
          }}
          getRowClassName={(params: any) => {
            return (params.id as number) % 2 === 0 ? "Mui-even" : "Mui-odd";
          }}
          columnBuffer={0}
          // autoHeight
        />

        <div className='w-full float-right mt-5'>
          <span className='font-bold'>Total ticket sales - {totalSalesTicketCount}</span>
          <span className='font-bold ml-6'>
            Total ticket sales price - {currencySymbol}
            {totalSalesPrice}
          </span>
        </div>
      </Grid>

      <Dialog
        open={openTicketChangePopup}
        onClose={() => setOpenTicketChangePopup(false)}
        aria-labelledby='ticket-popup-dialog-title'
        aria-describedby='ticket-popup-dialog-description'
      >
        <DialogTitle id='ticket-popup-dialog-title'>
          {t("updateTicketSalesDialogTitle")}
        </DialogTitle>
        <DialogContent>
          <CommonInput
            id='title-id-input-box'
            fullWidth
            name='endSalesNumber'
            label={t("endSalesNumber")}
            placeholder={t("endSalesNumber")}
            onChange={(e) => handleChange(e)}
            value={endSalesNumber}
            className='input-box'
            sx={true}
            error={errors !== "" ? true : false}
            helperText={errors}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTicketChangePopup(false)}>{t("cancel")}</Button>
          <Button
            onClick={() => updateTicketDetailsMechanism()}
            autoFocus
            disabled={errors !== "" ? true : false}
          >
            {t("save")} {updateTicketLoading && <CircularProgress size={10} color='primary' />}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
                open={endDayPopupOpen}
                onClose={() => setEndDayPopupOpen(false)}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogTitle id="alert-dialog-title">
                    {t('endDayPopupTitle')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="alert-dialog-description">
                        {t('deleteShopConfirmation')}
                    </DialogContentText>
                    <DialogContentText className="font-bold text-center">
                    <span className="mt-4">{t("endDayNote")}</span>
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setEndDayPopupOpen(false)}>{t('disagree')}</Button>
                    <Button onClick={() => endDayMainMechanism()} autoFocus>
                        {t('agree')}
                    </Button>
                </DialogActions>
            </Dialog>
    </>
  );
};

export default SalesInventory;
